import React, { useEffect, useState } from 'react';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import { ROUTES } from '../../constants';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LinkText,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import i18n from '../../utilities/i18n';
import { Study } from '../../models';
import { EntityStatus } from 'adc-sirius-module-common';
import { enumToCheckboxOptions } from '../../utilities/helpers';
import DynamicTable, {
  ColumnConfig,
  CheckboxFilterSection,
} from '../table/DynamicTable';
import DateCellRenderer from '../table/DateCellRenderer';
import { useQuery } from '@tanstack/react-query';
import {
  organizationCapabilitiesQueryOptions,
  studiesQueryOptions,
} from '../../queryOptions';
import { useAppSelector } from '../../reducers/hooks';

/**
 * Studies component that displays the details of all studies.
 * @returns {React.JSX.Element}
 */
export default function Studies() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const organizationCapabilities = useQuery(
    organizationCapabilitiesQueryOptions({
      key: useAppSelector((state) => state.organizations.currentOrgKey),
    }),
  );

  const [filteredStudies, setFilteredStudies] = useState<Study[]>([]);
  const [checkboxFilters, setCheckboxFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  const navigateToStudy = (studyKey: string, studyLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.STUDIES.DETAIL.replace(':studyKey', studyKey),
      studyLabel,
      i18n.t('Page_Studies.title'),
    );
  };

  const { currentOrgKey } = useAppSelector((state) => state.organizations);

  const studies = useQuery(
    studiesQueryOptions({ organizationKey: currentOrgKey }),
  );

  // apply filters to the studies data
  useEffect(() => {
    let filtered = studies.data || [];

    checkboxFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0) {
        if (filter.id === 'status') {
          filtered = filtered.filter((study) =>
            activeFilterValues.includes(String(study.status)),
          );
        } else {
          filtered = filtered.filter((study) =>
            activeFilterValues.includes(
              String(study[filter.id as keyof Study]),
            ),
          );
        }
      }
    });

    setFilteredStudies(filtered);
  }, [checkboxFilters, studies.data]);

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const studiesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (study: Study) => getStatusLabel(study.status),
    },
    { label: i18n.t('Entity.createdOn'), dataKey: 'createdOn' },
    { label: i18n.t('Entity.createdBy'), dataKey: 'createdBy' },
    { label: i18n.t('Entity.region'), dataKey: 'region' },
  ];
  const studiesTableCellRenderer = (dataKey: string, study: Study) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToStudy(study.key, study.name)}
            type="$bodySmall.default"
          >
            {study[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(study.status)}
        </Table.TextCell>
      );
    } else if (dataKey === 'createdOn') {
      return <DateCellRenderer value={study.createdOn} key={dataKey} />;
    } else {
      return <Table.TextCell key={dataKey}>{study[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <VStack height="100%">
      {studies.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="studies-list-loading" />
        </VStack>
      ) : null}

      {studies.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Studies.error_title')}
          body={i18n.t('Page_Studies.error_default')}
        />
      ) : null}

      {studies.isSuccess ? (
        <DynamicTable
          isLoading={studies.isPending}
          hasLoadingError={studies.isError}
          title={i18n.t('Page_Studies.title')}
          description={i18n.t('Page_Studies.table_description')}
          columnData={studiesTableColumnData}
          rowData={filteredStudies}
          cellRenderer={studiesTableCellRenderer}
          enableFiltering={true}
          checkboxFilters={checkboxFilters}
          onFiltersChange={setCheckboxFilters}
          onFiltersReset={() => {
            setCheckboxFilters((prevFilters) =>
              prevFilters.map((filter) => ({
                ...filter,
                items: filter.items.map((item) => ({ ...item, value: false })),
              })),
            );
          }}
          toolbarRightContent={
            organizationCapabilities.data?.canInsertStudy ? (
              <Button
                label={i18n.t('Page_Studies.create_study_button')}
                onPress={() =>
                  navigateWithBreadcrumbs(
                    ROUTES.STUDIES.CREATE,
                    i18n.t('Page_StudyCreate.wizard_title'),
                    i18n.t('Page_Studies.title'),
                  )
                }
                size="medium"
                testID="create-study"
              />
            ) : null
          }
        />
      ) : null}
    </VStack>
  );
}
